import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/office-space-for-lease-kitchener-waterloo',
  title: 'Office Space for Lease — Kitchener & Waterloo, ON',
  description:
    'Office leasing across Kitchener and Waterloo, from downtown Kitchener to the ION corridor. Direct line to Pinnacle Commercial, no listings portal.',
};

const kitchener = site.serviceAreas.find((a) => a.slug === 'kitchener')!;
const waterloo = site.serviceAreas.find((a) => a.slug === 'waterloo')!;

const FAQS: Faq[] = [
  {
    q: 'Do you cover both Kitchener and Waterloo, or just one?',
    a: 'Both — they function as one office market for most tenants, and splitting a search between them by broker would just slow you down. One conversation covers the whole corridor.',
  },
  {
    q: "What's the difference between downtown Kitchener and uptown Waterloo for an office tenant?",
    a: "Downtown Kitchener has more of the converted-industrial and new-build mix from the ongoing redevelopment; Uptown Waterloo and the university-adjacent nodes skew toward tech and professional tenants near the ION line. Which fits depends on your team and who you're trying to attract — worth a real conversation, not a guess from a map.",
  },
  {
    q: 'Do you have a current list of available office space?',
    a: "No public listings page. Office inventory here turns over quickly and a static list goes stale fast — a direct conversation gets you what's actually available today, not what was available when a page was last updated.",
  },
  {
    q: 'Can you help with sublease space, not just direct leases?',
    a: "Yes. Sublease terms can be some of the better value in this market, and they're exactly the kind of opportunity that never makes it onto a public listings page.",
  },
];

export default function OfficeKitchenerWaterloo() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-office-kw', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-02"
      kicker="Local search — office leasing"
      h1="Office space for lease, Kitchener & Waterloo."
      intro="One office market, two cities — downtown Kitchener's redevelopment corridor and Waterloo's ION-line and university-adjacent nodes, covered from inside the region, not managed remotely."
      glyph="office"
      audiences={[
        {
          label: 'Looking for space',
          body: "Headcount, timeline, and whether proximity to the ION line or the university matters to your team — tell me that and I'll tell you what fits in Kitchener and Waterloo right now.",
          ctaLabel: 'Tell me what you need',
        },
        {
          label: 'Have space to lease',
          body: 'Positioning and tenant sourcing for office space in either city, direct to qualified tenants rather than a blanket listing.',
          ctaLabel: 'Talk through your space',
        },
      ]}
      localLabel="Kitchener & Waterloo, office"
      localBody={
        <>
          <p>{kitchener.blurb}</p>
          <p>{waterloo.blurb}</p>
        </>
      }
      honestLabel="No listings page, on purpose"
      honestBody="Office availability across Kitchener and Waterloo moves fast, and a static page is wrong within days. Reach out with your requirements and you'll get the real current picture, including sublease and off-market space that never gets publicly posted."
      faqs={FAQS}
      interest="leasing"
      closingLine="Office space in Kitchener and Waterloo — one conversation, both cities."
      related={[
        { to: '/leasing', label: 'Full leasing services' },
        { to: '/retail-space-for-lease-uptown-waterloo-downtown-kitchener', label: 'Retail — Uptown Waterloo / Downtown Kitchener' },
        { to: '/industrial-space-for-lease-cambridge', label: 'Industrial — Cambridge' },
      ]}
    />
  );
}
